import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import get from 'lodash/get';
import {
  FetchPaymentMethodsOfTransactionAccountForDetailPageQuery,
  FetchTransactionAccountForAccountDetailsScreenQuery,
  PaymentMethod,
  PaymentMethodType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { NoData } from '@css-xp/ppc-common';
import {
  FormattedAddress,
  SummaryPanelContainer,
  SummaryPanelIcon,
  SummaryPanelRow,
  CopyableText,
  AchPanel,
} from '@css-xp/base';
import { routePaths } from '@css-xp/ppc-constants';
import { useGetPartnerAccountById } from '@css-xp/ppc-custom-hooks';
import { PaypalPaymentMethod } from '@css-xp/ppc-paypal';
import { PartnerAccount, LayoutAppearance } from '@css-xp/ppc-types';
import { TAX_UTILS, getRouteUrl, isPaypalPaymentMethod, isAchPaymentMethod } from '@css-xp/ppc-utils';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { getPaymentMethodOption } from './components/credit-card-payment';
import { AccountBalanceLabel, AccountBalanceData } from './components';
import './detail-page-summary.scss';

export interface Props {
  paymentMethodsData: FetchPaymentMethodsOfTransactionAccountForDetailPageQuery | undefined;
  partnerAccountData: PartnerAccount;
  paymentDataLoading: boolean;
  accountDetailsData: FetchTransactionAccountForAccountDetailsScreenQuery | undefined;
  accountDetailsLoading: boolean;
  partnerDomains: Array<string>;
  isPartnerAccountDomainsLoading?: boolean;
  label?: string;
  layoutAppearance?: LayoutAppearance;
  isIULPhase2Enabled?: boolean;
  shouldUseIULDetailsViaAuth?: boolean;
}

export const DetailPageSummary = ({
  paymentMethodsData,
  partnerAccountData,
  paymentDataLoading,
  accountDetailsData,
  accountDetailsLoading,
  partnerDomains,
  isPartnerAccountDomainsLoading,
  layoutAppearance = LayoutAppearance.DEFAULT,
  isIULPhase2Enabled,
  shouldUseIULDetailsViaAuth = false,
}: Props) => {
  const {
    partnerType,
    programLevelGlobal,
    verifiedExpertise,
    specializations,
    payOnAccount,
    transactionAccountId,
    transactionAccountNumber,
    internalUseLicense,
    id,
  } = partnerAccountData;
  const isOneColumnLayout = layoutAppearance === LayoutAppearance.ONE_COLUMN;

  const { partnerAccountDetails, partnerAccountDetailsLoading } = useGetPartnerAccountById({
    partnerAccountId: id,
    skip: shouldUseIULDetailsViaAuth,
  });

  const iulDetails = shouldUseIULDetailsViaAuth ? internalUseLicense : partnerAccountDetails?.internalUseLicense;

  const getDefaultPaymentSection = useCallback(() => {
    if (paymentDataLoading) {
      return (
        <SkeletonLoaderWrapper>
          <SkeletonLoader width="250px" height="100px" marginBottom="0px" />
        </SkeletonLoaderWrapper>
      );
    }
    const availablePaymentMethods = get(paymentMethodsData, 'transactionAccount.availablePaymentMethods.nodes', []);
    const defaultPaymentMethod = availablePaymentMethods.find((paymentMethod: PaymentMethod) => paymentMethod?.default);
    if (defaultPaymentMethod) {
      if (defaultPaymentMethod?.type === PaymentMethodType.CARD) {
        return getPaymentMethodOption({
          paymentMethod: defaultPaymentMethod,
          defaultPaymentMethodId: defaultPaymentMethod.id,
        });
      } else if (isPaypalPaymentMethod(defaultPaymentMethod?.type)) {
        return (
          <PaypalPaymentMethod
            emailID={defaultPaymentMethod?.payPal?.email}
            withBorder={true}
            renderChildrenInline={false}
          />
        );
      } else if (isAchPaymentMethod(defaultPaymentMethod?.type)) {
        return (
          <AchPanel
            bankName={defaultPaymentMethod?.ach?.bankName}
            last4={defaultPaymentMethod?.ach?.last4}
            showBorder
            showInfoInline={false}
            width="250px"
          />
        );
      }
    }
    return 'No payment method associated with this account';
  }, [paymentDataLoading, paymentMethodsData]);

  const getAccountBalance = useCallback(() => {
    if (paymentDataLoading) {
      return (
        <SkeletonLoaderWrapper>
          <SkeletonLoader width="200px" marginBottom="0" />
        </SkeletonLoaderWrapper>
      );
    }
    return (
      <AccountBalanceData customerBalances={get(paymentMethodsData, 'transactionAccount.customerBalances', {}) || {}} />
    );
  }, [paymentDataLoading, paymentMethodsData]);

  const getPricingEligibilitySection = useCallback(() => {
    if (accountDetailsLoading) {
      return (
        <SkeletonLoaderWrapper>
          <SkeletonLoader width="100px" marginBottom="0" />
        </SkeletonLoaderWrapper>
      );
    }

    return get(accountDetailsData, 'transactionAccount.billToPartyPricingEligibilty', null) || 'Commercial';
  }, [accountDetailsLoading, accountDetailsData]);

  const getBillingAddressSection = useCallback(() => {
    if (accountDetailsLoading) {
      return (
        <SkeletonLoaderWrapper>
          <SkeletonLoader height="80px" width="160px" marginBottom="0" />
        </SkeletonLoaderWrapper>
      );
    }

    const billToParty = accountDetailsData?.transactionAccount?.billToParty;

    return (
      <FormattedAddress
        addressLine1={billToParty?.addressLine1}
        addressLine2={billToParty?.addressLine2}
        city={billToParty?.city}
        country={billToParty?.country}
        postcode={billToParty?.postcode}
        state={billToParty?.state}
        organizationName={billToParty?.name}
        hasLineBreak={!isOneColumnLayout}
      />
    );
  }, [accountDetailsLoading, accountDetailsData, isOneColumnLayout]);

  const getTaxExemptSection = useCallback(() => {
    if (accountDetailsLoading) {
      return (
        <SkeletonLoaderWrapper>
          <SkeletonLoader width="100px" marginBottom="0" />
        </SkeletonLoaderWrapper>
      );
    }

    const taxLabelInformation = TAX_UTILS.getTaxLabelInformationFromAddress(
      accountDetailsData?.transactionAccount?.billToParty
    );

    return (
      <>
        {taxLabelInformation.map((tax, idx) => (
          <div key={`tax-exempt-${idx}`}>
            {tax.taxDisplayName ? `${tax.taxDisplayName} (${tax.taxExempt})` : tax.taxExempt}
          </div>
        ))}
      </>
    );
  }, [accountDetailsLoading, accountDetailsData]);

  const getPartnerAccountsDomains = useCallback(() => {
    if (isPartnerAccountDomainsLoading) {
      return (
        <SkeletonLoaderWrapper>
          <SkeletonLoader width="100px" marginBottom="0" />
        </SkeletonLoaderWrapper>
      );
    }

    if (!partnerDomains?.length) {
      return <NoData />;
    }

    return (
      <div>
        {partnerDomains?.map((item) => (
          <div key={item}>{item}</div>
        ))}
      </div>
    );
  }, [isPartnerAccountDomainsLoading, partnerDomains]);

  const getTaxDetailsSection = useCallback(() => {
    if (accountDetailsLoading) {
      return (
        <SummaryPanelRow
          label="Tax ID"
          value={
            <SkeletonLoaderWrapper>
              <SkeletonLoader width="100px" marginBottom="0" />
            </SkeletonLoaderWrapper>
          }
          testId="tax-id"
          isHiddenByDefault={!isOneColumnLayout}
          useWrapper={isOneColumnLayout}
        />
      );
    }

    const taxInformation = TAX_UTILS.getTaxInformationFromAddress(accountDetailsData?.transactionAccount?.billToParty);
    if (taxInformation.length > 0) {
      return taxInformation.map((tax, idx) => (
        <SummaryPanelRow
          label={tax.label}
          value={tax.value ?? <NoData />}
          testId={`tax-id-label-${idx}`}
          key={`tax-id-label-${idx}`}
          isHiddenByDefault={!isOneColumnLayout}
          useWrapper={isOneColumnLayout}
        />
      ));
    }

    return (
      <SummaryPanelRow
        label="Tax ID"
        value={<NoData />}
        testId="tax-id"
        isHiddenByDefault={!isOneColumnLayout}
        useWrapper={isOneColumnLayout}
      />
    );
  }, [accountDetailsLoading, accountDetailsData, isOneColumnLayout]);

  const getIULBalanceDetailsSection = useCallback(() => {
    if (!shouldUseIULDetailsViaAuth && partnerAccountDetailsLoading) {
      return (
        <SkeletonLoaderWrapper>
          <SkeletonLoader width="100px" marginBottom="0" />
        </SkeletonLoaderWrapper>
      );
    } else if (iulDetails) {
      return `${iulDetails.currencyIsoCode} ${(iulDetails.currentBalance ?? 0).toFixed(2)} / ${(
        iulDetails.startingBalance ?? 0
      ).toFixed(2)}`;
    }
    return <NoData />;
  }, [iulDetails, partnerAccountDetailsLoading, shouldUseIULDetailsViaAuth]);

  return (
    <div
      className={isOneColumnLayout ? 'detail-page-summary-v2' : 'detail-page-summary'}
      data-testid={isOneColumnLayout ? 'detail-page-summary-tab' : ''}
    >
      <div className={isOneColumnLayout ? '' : 'detail-container-section'}>
        <SummaryPanelContainer
          title={isOneColumnLayout ? undefined : 'Billing account overview'}
          iconKey={SummaryPanelIcon.SUBSCRIPTION_ICON}
          isHeaderContentHidden={isOneColumnLayout}
          version={isOneColumnLayout ? 2 : undefined}
        >
          <SummaryPanelRow
            label="Account number"
            value={<CopyableText text={transactionAccountNumber} />}
            testId="account-number"
            useWrapper={isOneColumnLayout}
          />
          <SummaryPanelRow
            label="Partner type"
            value={partnerType}
            testId="partner-type"
            useWrapper={isOneColumnLayout}
          />
          <SummaryPanelRow
            label="Verified expertise field"
            value={
              verifiedExpertise?.length ? (
                <div>
                  {verifiedExpertise.map((item) => (
                    <div key={item}>{item}</div>
                  ))}
                </div>
              ) : (
                <NoData />
              )
            }
            testId="verified-expertise-field"
            useWrapper={isOneColumnLayout}
          />
          <SummaryPanelRow
            label="Specialization field"
            value={
              specializations?.length ? (
                <div>
                  {specializations.map((item) => (
                    <div key={item}>{item}</div>
                  ))}
                </div>
              ) : (
                <NoData />
              )
            }
            testId="specialization-field"
            useWrapper={isOneColumnLayout}
          />
          <SummaryPanelRow
            label="Partner level"
            value={programLevelGlobal || <NoData />}
            testId="partner-level"
            useWrapper={isOneColumnLayout}
          />
          <SummaryPanelRow
            label="Billing address"
            value={getBillingAddressSection()}
            testId="billing-address"
            useWrapper={isOneColumnLayout}
          />
          {getTaxDetailsSection()}
          <SummaryPanelRow
            label="Tax exempt"
            value={getTaxExemptSection()}
            testId="tax-exempt"
            isHiddenByDefault={!isOneColumnLayout}
            useWrapper={isOneColumnLayout}
          />
          <SummaryPanelRow
            label="Pricing eligibility"
            value={getPricingEligibilitySection()}
            isHiddenByDefault={!isOneColumnLayout}
            testId="price-eligibility"
            useWrapper={isOneColumnLayout}
          />
          <SummaryPanelRow
            label="Pay on account"
            value={payOnAccount ? 'Yes' : 'No'}
            isHiddenByDefault={!isOneColumnLayout}
            testId="pay-on-account"
            useWrapper={isOneColumnLayout}
          />
          <SummaryPanelRow
            label={'Domains'}
            value={getPartnerAccountsDomains()}
            isHiddenByDefault={!isOneColumnLayout}
            testId="domain"
            useWrapper={isOneColumnLayout}
          />
        </SummaryPanelContainer>
      </div>
      <div className="detail-container-section">
        <SummaryPanelContainer
          title={isOneColumnLayout ? undefined : 'Financial info'}
          iconKey={SummaryPanelIcon.BILLING_PAYMENT_ICON}
          isHeaderContentHidden={isOneColumnLayout}
        >
          <SummaryPanelRow
            label="Default payment method"
            value={<div className="default-payment-method">{getDefaultPaymentSection()}</div>}
            testId="default-payment-method"
            useWrapper={isOneColumnLayout}
          />
          <SummaryPanelRow
            label={<AccountBalanceLabel version={isOneColumnLayout ? 2 : undefined} />}
            value={
              <div className="account-balance-section">
                <div className="account-balance-value">{getAccountBalance()}</div>
                <Link
                  to={getRouteUrl(routePaths.accountBalance, { txaId: transactionAccountId })}
                  aria-label="View account credit balance details"
                >
                  View details
                </Link>
              </div>
            }
            testId="account-balance"
            useWrapper={isOneColumnLayout}
          />
          {isIULPhase2Enabled && (
            <SummaryPanelRow
              label="Internal usage balance"
              value={getIULBalanceDetailsSection()}
              testId="internal-licenses-balance"
              useWrapper={isOneColumnLayout}
            />
          )}
        </SummaryPanelContainer>
      </div>
    </div>
  );
};
